import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import {
  Receipt, Plus, CheckCircle, Clock, Warning, CurrencyDollar,
  Eye, CreditCard, Bank, Download,
} from '@phosphor-icons/react';
import api from '../services/api';
import { useAgency } from '../context/AgencyContext';
import { StatsCardGrid } from '../components/shared/StatsCardGrid';
import { logger } from '../lib/logger';
import {
  CreateInvoiceDialog,
  ViewInvoiceDialog,
  RecordPaymentDialog,
} from '../components/azai-billing/AZAIBillingDialogs';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: Clock },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: Warning },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500', icon: Warning },
};

const PAYMENT_METHODS = [
  { value: 'ach', label: 'ACH Bank Transfer', icon: Bank },
  { value: 'credit_card', label: 'Credit Card', icon: CreditCard },
  { value: 'check', label: 'Check', icon: Receipt },
  { value: 'wire', label: 'Wire Transfer', icon: Bank },
];

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const SUMMARY_CARDS = [
  { key: 'total_invoices', label: 'Total Invoices', icon: Receipt, color: 'indigo', isCurrency: false },
  { key: 'total_billed', label: 'Total Billed', icon: CurrencyDollar, color: 'blue', isCurrency: true },
  { key: 'total_paid', label: 'Collected', icon: CheckCircle, color: 'green', isCurrency: true },
  { key: 'total_pending', label: 'Pending', icon: Clock, color: 'amber', isCurrency: true },
  { key: 'collection_rate', label: 'Collection Rate', icon: CurrencyDollar, color: 'emerald', isCurrency: false, suffix: '%' },
];

const AZAIBillingPage = () => {
  const { agencies, isAzaiStaff, azaiRole } = useAgency();
  const [invoices, setInvoices] = useState([]);
  const [billingSummary, setBillingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [agencyFilter, setAgencyFilter] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [formData, setFormData] = useState({
    agency_id: '',
    period_start: '',
    period_end: '',
    due_date: '',
    line_items: [{ description: 'Monthly Billing Service Fee', quantity: 1, unit_price: 0, total: 0 }],
    notes: '',
  });

  const [paymentData, setPaymentData] = useState({
    payment_method: 'ach', transaction_id: '', notes: '',
  });

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (agencyFilter !== 'all') params.agency_id = agencyFilter;
      const response = await api.get('/agencies/billing/invoices', { params });
      setInvoices(response.data.invoices || []);
    } catch {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, agencyFilter]);

  const loadBillingSummary = useCallback(async () => {
    try {
      const response = await api.get('/agencies/billing/summary');
      setBillingSummary(response.data);
    } catch (error) {
      logger.error('Failed to load billing summary:', error);
    }
  }, []);

  useEffect(() => {
    if (isAzaiStaff) {
      loadInvoices();
      loadBillingSummary();
    }
  }, [isAzaiStaff, loadInvoices, loadBillingSummary]);

  const resetForm = () => {
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const dueDate = new Date(today.getFullYear(), today.getMonth() + 1, 15);
    setFormData({
      agency_id: '',
      period_start: firstOfMonth.toISOString().split('T')[0],
      period_end: lastOfMonth.toISOString().split('T')[0],
      due_date: dueDate.toISOString().split('T')[0],
      line_items: [{ description: 'Monthly Billing Service Fee', quantity: 1, unit_price: 500, total: 500 }],
      notes: '',
    });
  };

  const updateLineItem = (index, field, value) => {
    const items = [...formData.line_items];
    items[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      items[index].total = items[index].quantity * items[index].unit_price;
    }
    setFormData({ ...formData, line_items: items });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0, total: 0 }],
    });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length <= 1) return;
    setFormData({ ...formData, line_items: formData.line_items.filter((_, i) => i !== index) });
  };

  const calculateTotal = () => formData.line_items.reduce((sum, item) => sum + (item.total || 0), 0);

  const handleCreateInvoice = async () => {
    if (!formData.agency_id) { toast.error('Please select an agency'); return; }
    try {
      await api.post('/agencies/billing/invoices', formData);
      toast.success('Invoice created successfully');
      setCreateOpen(false);
      loadInvoices();
      loadBillingSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedInvoice) return;
    try {
      await api.post(`/agencies/billing/invoices/${selectedInvoice.id}/pay`, paymentData);
      toast.success('Payment recorded successfully');
      setPaymentOpen(false);
      loadInvoices();
      loadBillingSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({ payment_method: invoice.payment_method || 'ach', transaction_id: '', notes: '' });
    setPaymentOpen(true);
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await api.get(`/agencies/billing/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const canManageBilling = azaiRole === 'super_admin' || azaiRole === 'billing_manager';

  if (!isAzaiStaff) {
    return (
      <div className="p-8 text-center">
        <Receipt size={48} className="mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">Only AZAI staff can access billing management.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="azai-billing-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>AZAI Billing</h1>
          <p className="text-slate-500 mt-1">Manage agency invoices and payments</p>
        </div>
        {canManageBilling && (
          <Button onClick={() => { resetForm(); setCreateOpen(true); }} data-testid="create-invoice-btn">
            <Plus size={18} className="mr-2" />New Invoice
          </Button>
        )}
      </div>

      {billingSummary && (
        <StatsCardGrid
          columns={5}
          cards={SUMMARY_CARDS.map((c) => ({
            label: c.label,
            value: billingSummary[c.key],
            icon: c.icon,
            color: c.color,
            isCurrency: c.isCurrency,
            suffix: c.suffix,
            testid: `azai-stat-${c.key}`,
          }))}
        />
      )}

      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={agencyFilter} onValueChange={setAgencyFilter}>
              <SelectTrigger className="w-48" data-testid="agency-filter"><SelectValue placeholder="All Agencies" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agencies</SelectItem>
                {agencies.map((agency) => (
                  <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([value, cfg]) => (
                  <SelectItem key={value} value={value}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-0"><CardTitle className="text-lg">Invoices</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="py-12 text-center">
              <Receipt size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No invoices found</p>
              {canManageBilling && (
                <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setCreateOpen(true); }}>
                  Create First Invoice
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => {
                  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.pending;
                  const StatusIcon = statusConfig.icon;
                  return (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-mono text-sm">{invoice.id}</TableCell>
                      <TableCell>{invoice.agency_name}</TableCell>
                      <TableCell className="text-sm">
                        {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                      </TableCell>
                      <TableCell>{formatDate(invoice.due_date)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(invoice.total)}</TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          <StatusIcon size={12} className="mr-1" />{statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedInvoice(invoice); setViewOpen(true); }} title="View details">
                            <Eye size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(invoice)} title="Download PDF" data-testid={`download-pdf-${invoice.id}`}>
                            <Download size={16} />
                          </Button>
                          {invoice.status === 'pending' && canManageBilling && (
                            <Button variant="ghost" size="sm" onClick={() => openPaymentDialog(invoice)} className="text-green-600" title="Record payment">
                              <CheckCircle size={16} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateInvoiceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        agencies={agencies}
        formData={formData}
        setFormData={setFormData}
        onAddLineItem={addLineItem}
        onUpdateLineItem={updateLineItem}
        onRemoveLineItem={removeLineItem}
        calculateTotal={calculateTotal}
        onCreate={handleCreateInvoice}
      />

      <ViewInvoiceDialog
        open={viewOpen}
        onOpenChange={setViewOpen}
        invoice={selectedInvoice}
        statusConfig={STATUS_CONFIG}
        paymentMethods={PAYMENT_METHODS}
        onDownload={handleDownloadPdf}
      />

      <RecordPaymentDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        invoice={selectedInvoice}
        paymentData={paymentData}
        setPaymentData={setPaymentData}
        paymentMethods={PAYMENT_METHODS}
        onRecord={handleRecordPayment}
      />
    </div>
  );
};

export default AZAIBillingPage;
